import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { fetchData } from '../../../apiService';

// Number to Words Conversion
const NumberToWords = (num) => {
    const units = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
    const teens = ['Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const tens = ['', 'Ten', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    const thousands = ['', 'Thousand', 'Million', 'Billion'];

    const convert = (num) => {
        if (num === 0) return '';
        if (num < 10) return units[num];
        if (num === 10) return tens[1];
        if (num < 20) return teens[num - 11];
        if (num < 100) return tens[Math.floor(num / 10)] + (num % 10 !== 0 ? ' ' + units[num % 10] : '');
        if (num < 1000) return units[Math.floor(num / 100)] + ' Hundred' + (num % 100 !== 0 ? ' ' + convert(num % 100) : '');

        // Handle larger numbers
        let result = '';
        let thousandIndex = 0;
        while (num > 0) {
            const chunk = num % 1000;
            if (chunk !== 0) {
                const chunkWords = convert(chunk);
                result = chunkWords + (thousands[thousandIndex] ? ' ' + thousands[thousandIndex] : '') + (result ? ' ' + result : '');
            }
            num = Math.floor(num / 1000);
            thousandIndex++;
        }
        return result;
    };

    if (num === 0) return 'Zero';
    if (num < 0) return 'Minus ' + convert(Math.abs(num)).trim();
    return convert(num).trim();
};

// Convert Amount to Indian Rupees
const ConvertAmountToRupees = (amount) => {
    const numericAmount = parseFloat(amount);

    if (isNaN(numericAmount)) return 'Invalid Amount';

    const isNegative = numericAmount < 0;
    const absAmount = Math.abs(numericAmount);

    const formattedAmount = absAmount.toFixed(2);
    const [rupees, paise] = formattedAmount.split('.');

    let words = `${NumberToWords(parseInt(rupees))} Rupees`;
    if (paise && parseInt(paise) > 0) {
        words += ` and ${NumberToWords(parseInt(paise))} Paise`;
    }

    return (isNegative ? 'Minus ' : '') + words + ' Only';
};

// Main Invoice Generation Function
export const generateInvoice = async (saleId, token) => {
    try {
        // Fetch Data
        const response = await fetchData('InvoiceApi', `/SalePrint/${saleId}`, token);
        const labelData = response.response_data;

        if (!labelData?.SalePrint || !labelData?.SalePrintItems) {
            throw new Error("Invalid invoice data.");
        }

        const header = labelData.SalePrint;
        const items = labelData.SalePrintItems;

        // Initialize PDF
        const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
        let pageWidth = doc.internal.pageSize.getWidth();
        let pageHeight = doc.internal.pageSize.getHeight();

        const margin = 10;
        const contentWidth = pageWidth - (margin * 2);

        let y = 15;

        // Elegant Header with Medium Accent Color Bar
        doc.setFillColor(66, 165, 245); // Medium blue
        doc.rect(0, 0, pageWidth, 35, 'F');

        // Company/Invoice Title
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(25).setFont("helvetica", "bold");
        doc.text("INVOICE", margin, 18);

        // Decorative line under title
        // doc.setDrawColor(255, 255, 255);
        // doc.setLineWidth(0.8);
        // doc.line(margin, 22, margin + 45, 22);

        // Reset text color
        doc.setTextColor(0, 0, 0);
        y = 45;

        // Invoice Details Box (Top Right)
        const boxX = pageWidth - margin - 70;
        const boxY = y - 5;
        doc.setFillColor(245, 248, 252);
        doc.roundedRect(boxX, boxY, 70, 28, 2, 2, 'F');

        doc.setDrawColor(66, 165, 245);
        doc.setLineWidth(0.5);
        doc.roundedRect(boxX, boxY, 70, 28, 2, 2, 'S');

        // Invoice Number
        doc.setFontSize(9).setFont("helvetica", "bold");
        doc.setTextColor(66, 165, 245);
        doc.text("INVOICE NO.", boxX + 3, boxY + 6);
        doc.setFontSize(11).setFont("helvetica", "bold");
        doc.setTextColor(0, 0, 0);
        doc.text(header.BillNumber || 'N/A', boxX + 3, boxY + 12);

        // Date
        doc.setFontSize(9).setFont("helvetica", "bold");
        doc.setTextColor(66, 165, 245);
        doc.text("DATE", boxX + 3, boxY + 19);
        doc.setFontSize(10).setFont("helvetica", "normal");
        doc.setTextColor(0, 0, 0);
        const formattedDate = header.BillDate ? new Date(header.BillDate).toLocaleDateString('en-GB') : '';
        doc.text(formattedDate, boxX + 3, boxY + 25);

        // Bill To Section with elegant styling
        y += 5;
        doc.setFontSize(10).setFont("helvetica", "bold");
        doc.setTextColor(66, 165, 245);
        doc.text("BILL TO", margin, y);

        // Underline for Bill To
        doc.setDrawColor(66, 165, 245);
        doc.setLineWidth(0.5);
        doc.line(margin, y + 1, margin + 18, y + 1);

        y += 7;
        doc.setTextColor(0, 0, 0);
        doc.setFontSize(12).setFont("helvetica", "bold");
        doc.text(header.CustomerName || 'N/A', margin, y);

        y += 6;
        doc.setFontSize(10).setFont("helvetica", "normal");
        doc.setTextColor(60, 60, 60);

        const addressLines = doc.splitTextToSize(header.CustomerAddress || '', contentWidth * 0.55);
        addressLines.forEach(line => {
            doc.text(line, margin, y);
            y += 5;
        });

        y += 8;

        // Items Table with Modern Styling
        const itemRows = items.map((item) => [
            new Date(item.RegisterDate).toLocaleDateString('en-GB'),
            item.ItemName || '',
            (item.Quantity ?? 0).toString(),
            (item.Rate ?? 0).toFixed(2),
            (item.TransportationCharges ?? 0).toFixed(2),
            (item.Amount ?? 0).toFixed(2)
        ]);

        const total = items.reduce((sum, i) => sum + (i.Amount ?? 0), 0);
        const transportTotal = items.reduce((sum, i) => sum + (i.TransportationCharges ?? 0), 0);
        const grandTotal = total + transportTotal;

        autoTable(doc, {
            startY: y,
            head: [['Date', 'Item', 'Qty', 'Rate', 'Transport', 'Total']],
            body: itemRows,
            foot: [
                ['', '', '', '', 'Total', total.toFixed(2)],
                ['', '', '', '', 'Transportation Charge', transportTotal.toFixed(2)],
                ['', '', '', '', 'Grand Total', grandTotal.toFixed(2)]
            ],
            styles: {
                font: "helvetica",
                fontSize: 9,
                lineColor: [230, 230, 230],
                textColor: [50, 50, 50],
                lineWidth: 0.1,
                cellPadding: 2,
                halign: 'center',
                minCellHeight: 7
            },
            headStyles: {
                fillColor: [66, 165, 245],
                textColor: [255, 255, 255],
                fontStyle: "bold",
                fontSize: 10,
                lineWidth: 0,
                halign: 'center',
                cellPadding: 2,
                minCellHeight: 7
            },
            bodyStyles: {
                fontSize: 9,
                halign: 'center',
                fillColor: [255, 255, 255],
                cellPadding: 2,
                minCellHeight: 7
            },
            alternateRowStyles: {
                fillColor: [245, 248, 252]
            },
            footStyles: {
                fillColor: [66, 165, 245],
                textColor: [255, 255, 255],
                fontStyle: "bold",
                fontSize: 10,
                lineWidth: 0,
                cellPadding: 2,
                minCellHeight: 7
            },
            columnStyles: {
                0: { cellWidth: 25, halign: 'center' },
                1: { cellWidth: 'auto', halign: 'left', cellPadding: { left: 3, right: 2, top: 2, bottom: 2 } },
                2: { cellWidth: 15, halign: 'center' },
                3: { cellWidth: 18, halign: 'right' },
                4: { cellWidth: 45, halign: 'right' },
                5: { cellWidth: 25, halign: 'right', fontStyle: 'bold' }
            },
            theme: "plain",
            margin: { left: margin, right: margin },
            didDrawCell: (data) => {
                // Apply different blue shades to footer rows
                if (data.section === 'foot') {
                    if (data.row.index === 0) {
                        // Light blue for Total row
                        doc.setFillColor(100, 181, 246);
                        doc.rect(data.cell.x, data.cell.y, data.cell.width, data.cell.height, 'F');
                        doc.setTextColor(255, 255, 255);
                        doc.setFontSize(10);
                        doc.setFont("helvetica", "bold");
                        if (data.column.index === 4) {
                            doc.text(data.cell.text[0], data.cell.x + data.cell.width - 2, data.cell.y + data.cell.height / 2 + 2, { align: 'right' });
                        } else if (data.column.index === 5) {
                            doc.text(data.cell.text[0], data.cell.x + data.cell.width - 2, data.cell.y + data.cell.height / 2 + 2, { align: 'right' });
                        }
                    } else if (data.row.index === 1) {
                        // Medium blue for Transportation Charge row
                        doc.setFillColor(66, 165, 245);
                        doc.rect(data.cell.x, data.cell.y, data.cell.width, data.cell.height, 'F');
                        doc.setTextColor(255, 255, 255);
                        doc.setFontSize(10);
                        doc.setFont("helvetica", "bold");
                        if (data.column.index === 4) {
                            doc.text(data.cell.text[0], data.cell.x + data.cell.width - 2, data.cell.y + data.cell.height / 2 + 2, { align: 'right' });
                        } else if (data.column.index === 5) {
                            doc.text(data.cell.text[0], data.cell.x + data.cell.width - 2, data.cell.y + data.cell.height / 2 + 2, { align: 'right' });
                        }
                    } else if (data.row.index === 2) {
                        // Dark blue for Grand Total row
                        doc.setFillColor(33, 150, 243);
                        doc.rect(data.cell.x, data.cell.y, data.cell.width, data.cell.height, 'F');
                        doc.setTextColor(255, 255, 255);
                        doc.setFontSize(10);
                        doc.setFont("helvetica", "bold");
                        if (data.column.index === 4) {
                            doc.text(data.cell.text[0], data.cell.x + data.cell.width - 2, data.cell.y + data.cell.height / 2 + 2, { align: 'right' });
                        } else if (data.column.index === 5) {
                            doc.text(data.cell.text[0], data.cell.x + data.cell.width - 2, data.cell.y + data.cell.height / 2 + 2, { align: 'right' });
                        }
                    }
                }
            }
        });

        y = doc.lastAutoTable.finalY + 12;

        // Amount in Words Box
        doc.setFillColor(245, 248, 252);
        doc.roundedRect(margin, y, contentWidth, 18, 2, 2, 'F');
        doc.setDrawColor(66, 165, 245);
        doc.setLineWidth(0.5);
        doc.roundedRect(margin, y, contentWidth, 18, 2, 2, 'S');

        doc.setFontSize(9).setFont("helvetica", "bold");
        doc.setTextColor(66, 165, 245);
        doc.text("Amount in Words:", margin + 3, y + 6);

        doc.setFontSize(10).setFont("helvetica", "bold");
        doc.setTextColor(0, 0, 0);
        const amountInWords = ConvertAmountToRupees(grandTotal);
        const wordsLines = doc.splitTextToSize(amountInWords, contentWidth - 50);
        doc.text(wordsLines, margin + 3, y + 13);

        y += 25;

        // Terms & Conditions (Optional)
        doc.setFontSize(8).setFont("helvetica", "italic");
        doc.setTextColor(120, 120, 120);
        doc.text("Payment Terms: Due upon receipt | Thank you for your business!", margin, y);

        // Signature Section with elegant styling
        const signatureY = pageHeight - 45;

        // Signature box
        const sigBoxWidth = 70;
        const sigBoxX = pageWidth - margin - sigBoxWidth;

        doc.setDrawColor(220, 220, 220);
        doc.setLineWidth(0.5);
        doc.line(sigBoxX, signatureY, pageWidth - margin, signatureY);

        doc.setFontSize(10).setFont("helvetica", "bold");
        doc.setTextColor(0, 0, 0);
        doc.text(header.AuthorizedSignatory || 'Dhanya . C .P', pageWidth - margin, signatureY + 6, { align: "right" });

        doc.setFontSize(9).setFont("helvetica", "normal");
        doc.setTextColor(120, 120, 120);
        doc.text('Managing Director', pageWidth - margin, signatureY + 11, { align: "right" });

        doc.setFontSize(8).setFont("helvetica", "bold");
        doc.setTextColor(66, 165, 245);
        doc.text('Authorized Signature', pageWidth - margin, signatureY + 16, { align: "right" });

        // Elegant Footer
        doc.setFillColor(66, 165, 245);
        doc.rect(0, pageHeight - 20, pageWidth, 20, 'F');

        doc.setFontSize(9).setFont("helvetica", "normal");
        doc.setTextColor(255, 255, 255);
        doc.text('Thank you for your business!', pageWidth / 2, pageHeight - 11, { align: "center" });

        doc.setFontSize(7);
        doc.text('This is a computer-generated invoice and does not require a physical signature.', pageWidth / 2, pageHeight - 6, { align: "center" });

        // Open & Print
        const blob = doc.output('blob');
        const blobUrl = URL.createObjectURL(blob);
        const printWindow = window.open(blobUrl, '_blank');
        if (printWindow) {
            printWindow.onload = () => {
                printWindow.focus();
                printWindow.print();
            };
        }

    } catch (err) {
        console.error("Error generating invoice:", err);
        throw err;
    }
};
